import { GX_RUNTIME_MIN, GX_RUNTIME_VERSION } from '@/lib/video-runtime/runtime.min';
import { GX_STYLES } from '@/lib/video-runtime/styles';

export interface HyperframesAudioTrackInput {
  url: string;
  startSec: number;
  durationSec?: number;
  gainDb?: number;
}

const AUDIO_TRACK_ID_PREFIX = '__hf-audio-';

export function hyperframesGainDbToVolume(gainDb: number | undefined) {
  if (gainDb === undefined) return 1;
  return Math.round(Math.min(1, Math.max(0, 10 ** (gainDb / 20))) * 1000) / 1000;
}

function escapeHtmlAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Injects audio tracks as `<audio data-start ...>` elements directly inside
 * the composition root. Both playback paths key off these elements:
 * - Lambda MP4 export: the HyperFrames producer discovers `audio[data-start]`,
 *   applies `data-volume`, and muxes the mix into the MP4 (plan emits
 *   audio.aac, assemble muxes it — @hyperframes/aws-lambda 0.6.x).
 * - In-app preview: `HyperframesRenderer` plays the same tracks (parent-side)
 *   synced to the seek bar; the iframe copies stay paused/silent.
 *
 * Idempotent: if the document already contains injected tracks (saved
 * visualizations persist the injected form), it is returned unchanged.
 */
export function injectHyperframesAudioTracks(
  htmlDocument: string,
  tracks: HyperframesAudioTrackInput[],
) {
  if (tracks.length === 0) return htmlDocument;
  if (htmlDocument.includes(`id="${AUDIO_TRACK_ID_PREFIX}`)) return htmlDocument;

  const tags = tracks
    .map((track, index) => {
      const attrs = [
        `id="${AUDIO_TRACK_ID_PREFIX}${index}"`,
        `src="${escapeHtmlAttribute(track.url)}"`,
        `data-start="${track.startSec}"`,
        track.durationSec !== undefined ? `data-duration="${track.durationSec}"` : null,
        `data-volume="${hyperframesGainDbToVolume(track.gainDb)}"`,
        'preload="auto"',
        'crossorigin="anonymous"',
      ]
        .filter(Boolean)
        .join(' ');
      return `<audio ${attrs}></audio>`;
    })
    .join('\n');

  // Audio must be a direct child of the composition root for the export pipeline.
  const rootMatch = htmlDocument.match(/<[a-zA-Z][^>]*data-composition-id[^>]*>/);
  if (rootMatch && rootMatch.index !== undefined) {
    const insertAt = rootMatch.index + rootMatch[0].length;
    return `${htmlDocument.slice(0, insertAt)}\n${tags}${htmlDocument.slice(insertAt)}`;
  }

  const bodyClose = htmlDocument.toLowerCase().lastIndexOf('</body>');
  if (bodyClose !== -1) {
    return `${htmlDocument.slice(0, bodyClose)}${tags}\n${htmlDocument.slice(bodyClose)}`;
  }

  return `${htmlDocument}\n${tags}`;
}

const GX_RUNTIME_SCRIPT_ID = '__gx_runtime';

/** True when the document calls into the `gx` motion runtime (e.g. `gx.init(`, `gx.rise(`). */
export function documentReferencesGx(htmlDocument: string): boolean {
  return /\bgx\s*\.\s*[a-zA-Z_$]/.test(htmlDocument);
}

const GX_STYLES_ID = '__gx_styles';
const GX_CLASS_PATTERN = /\bgx-(center|split|stack|card|headline|kicker|caption|stat)\b/;

/** True when the document uses the gx layout/typography utility classes. */
export function documentReferencesGxClasses(htmlDocument: string): boolean {
  return GX_CLASS_PATTERN.test(htmlDocument);
}

/**
 * Inlines the gx utility stylesheet (lib/video-runtime/styles.ts) when a
 * gx- layout/typo class is referenced. Theme values arrive at runtime via
 * the CSS custom properties gx.init sets. Idempotent; class-free documents
 * are returned unchanged.
 */
export function injectGxStyles(htmlDocument: string): string {
  if (htmlDocument.includes(`id="${GX_STYLES_ID}"`)) return htmlDocument;
  if (!documentReferencesGxClasses(htmlDocument)) return htmlDocument;

  const tag = `<style id="${GX_STYLES_ID}">${GX_STYLES}</style>`;

  // End of <head> preferred (before any author styles' </head>), so author
  // CSS can still override the utilities.
  const headClose = htmlDocument.search(/<\/head>/i);
  if (headClose !== -1) {
    return `${htmlDocument.slice(0, headClose)}${tag}\n${htmlDocument.slice(headClose)}`;
  }

  const bodyOpen = htmlDocument.match(/<body[^>]*>/i);
  if (bodyOpen && bodyOpen.index !== undefined) {
    const insertAt = bodyOpen.index + bodyOpen[0].length;
    return `${htmlDocument.slice(0, insertAt)}\n${tag}${htmlDocument.slice(insertAt)}`;
  }

  return `${tag}\n${htmlDocument}`;
}

/**
 * Inlines the gx assets a document depends on — the minified motion runtime
 * (lib/video-runtime/runtime.min.ts) when `gx.` is called, and the utility
 * stylesheet when gx- classes are used — so compositions carry their
 * vocabulary with them: no network dependency at preview time or inside the
 * Lambda render.
 *
 * Called AT SAVE TIME by the hyperframes tool (and by the scene renderer,
 * whose emitted documents call gx), and again — idempotently — by the
 * preview renderer so raw tool-input documents work inline too.
 *
 * Old documents that never mention gx are returned byte-identical.
 */
export function injectVideoRuntime(htmlDocument: string): string {
  return injectGxStyles(injectGxRuntimeScript(htmlDocument));
}

function injectGxRuntimeScript(htmlDocument: string): string {
  if (htmlDocument.includes(`id="${GX_RUNTIME_SCRIPT_ID}"`)) return htmlDocument;
  if (!documentReferencesGx(htmlDocument)) return htmlDocument;

  const tag = `<script id="${GX_RUNTIME_SCRIPT_ID}" data-gx-version="${GX_RUNTIME_VERSION}">${GX_RUNTIME_MIN}</script>`;

  // Prefer the slot right after the GSAP CDN <script src> (gx only touches
  // gsap at call time, but this keeps execution order intuitive)...
  const gsapTag = htmlDocument.match(/<script[^>]*\bsrc="[^"]*gsap[^"]*"[^>]*>\s*<\/script>/i);
  if (gsapTag && gsapTag.index !== undefined) {
    const insertAt = gsapTag.index + gsapTag[0].length;
    return `${htmlDocument.slice(0, insertAt)}\n${tag}${htmlDocument.slice(insertAt)}`;
  }

  // ...otherwise before the FIRST script so gx exists before any caller runs.
  const firstScript = htmlDocument.search(/<script\b/i);
  if (firstScript !== -1) {
    return `${htmlDocument.slice(0, firstScript)}${tag}\n${htmlDocument.slice(firstScript)}`;
  }

  const bodyClose = htmlDocument.toLowerCase().lastIndexOf('</body>');
  if (bodyClose !== -1) {
    return `${htmlDocument.slice(0, bodyClose)}${tag}\n${htmlDocument.slice(bodyClose)}`;
  }

  return `${htmlDocument}\n${tag}`;
}

export interface HyperframesDocumentOptions {
  htmlDocument: string;
  width: number;
  height: number;
  durationSeconds: number;
  variables?: Record<string, unknown>;
}

function injectAfterOpeningTag(source: string, tagName: string, injection: string) {
  const pattern = new RegExp(`<${tagName}[^>]*>`, 'i');
  const match = source.match(pattern);
  if (!match || match.index === undefined) return `${injection}\n${source}`;

  const insertAt = match.index + match[0].length;
  return `${source.slice(0, insertAt)}\n${injection}\n${source.slice(insertAt)}`;
}

function injectBeforeClosingTag(source: string, tagName: string, injection: string) {
  const pattern = new RegExp(`</${tagName}>`, 'i');
  const match = source.match(pattern);
  if (!match || match.index === undefined) return `${source}\n${injection}`;

  return `${source.slice(0, match.index)}\n${injection}\n${source.slice(match.index)}`;
}

export function buildHyperframesPreviewDocument({
  htmlDocument,
  width,
  height,
  durationSeconds,
  variables = {},
}: HyperframesDocumentOptions) {
  const serializedVariables = JSON.stringify(variables).replace(/</g, '\\u003c');

  const prelude = `
<style id="__galexia_hyperframes_preview_base">
  html, body {
    margin: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: transparent;
  }

  [data-composition-id] {
    position: relative;
    width: ${width}px;
    height: ${height}px;
    overflow: hidden;
    box-sizing: border-box;
    transform-origin: top left;
  }

  [data-track-index] {
    position: absolute;
  }

  video, audio, img, canvas, svg {
    max-width: 100%;
  }
</style>
<script id="__galexia_hyperframes_preview_runtime">
  window.__timelines = window.__timelines || {};
  window.__hyperframes = window.__hyperframes || {};
  window.__hyperframes.getVariables = function () {
    return ${serializedVariables};
  };
</script>`.trim();

  const controller = `
<script id="__galexia_hyperframes_preview_controller">
(function () {
  var compositionWidth = ${JSON.stringify(width)};
  var compositionHeight = ${JSON.stringify(height)};
  var durationSeconds = ${JSON.stringify(durationSeconds)};
  var root = document.querySelector('[data-composition-id]');
  var shell = null;
  var scaleNode = null;
  var resolvedStarts = new Map();

  function toNumber(value, fallback) {
    var n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function parseRelativeStart(value) {
    if (!value || /^-?\\d+(\\.\\d+)?$/.test(String(value).trim())) {
      return { id: null, offset: toNumber(value, 0) };
    }

    var match = String(value).trim().match(/^([a-zA-Z0-9_-]+)\\s*([+-])?\\s*(\\d+(?:\\.\\d+)?)?$/);
    if (!match) return { id: null, offset: 0 };

    var offset = match[3] ? Number(match[3]) : 0;
    if (match[2] === '-') offset = -offset;
    return { id: match[1], offset: offset };
  }

  function getClipDuration(el) {
    return Math.max(0, toNumber(el.getAttribute('data-duration'), 0));
  }

  function getClipStart(el, depth) {
    if (resolvedStarts.has(el)) return resolvedStarts.get(el);
    if (depth > 8) return 0;

    var parsed = parseRelativeStart(el.getAttribute('data-start'));
    if (!parsed.id) {
      resolvedStarts.set(el, parsed.offset);
      return parsed.offset;
    }

    var reference = document.getElementById(parsed.id);
    if (!reference) {
      resolvedStarts.set(el, parsed.offset);
      return parsed.offset;
    }

    var start = getClipStart(reference, depth + 1) + getClipDuration(reference) + parsed.offset;
    resolvedStarts.set(el, start);
    return start;
  }

  function prepareStage() {
    root = root || document.querySelector('[data-composition-id]');
    if (!root) return;

    compositionWidth = toNumber(root.getAttribute('data-width'), compositionWidth);
    compositionHeight = toNumber(root.getAttribute('data-height'), compositionHeight);
    durationSeconds = toNumber(root.getAttribute('data-duration'), durationSeconds);

    if (!shell) {
      shell = document.createElement('div');
      shell.id = '__hf_shell';
      shell.style.cssText = 'width:100vw;height:100vh;overflow:hidden;display:flex;align-items:center;justify-content:center;background:transparent;';

      scaleNode = document.createElement('div');
      scaleNode.id = '__hf_scale';
      scaleNode.style.cssText = 'transform-origin:center center;';

      root.parentNode.insertBefore(shell, root);
      shell.appendChild(scaleNode);
      scaleNode.appendChild(root);
    }

    root.style.width = compositionWidth + 'px';
    root.style.height = compositionHeight + 'px';
    scaleNode.style.width = compositionWidth + 'px';
    scaleNode.style.height = compositionHeight + 'px';
    resizeStage();
  }

  function resizeStage() {
    if (!scaleNode) return;
    var scale = Math.min(window.innerWidth / compositionWidth, window.innerHeight / compositionHeight);
    if (!Number.isFinite(scale) || scale <= 0) scale = 1;
    scaleNode.style.transform = 'scale(' + scale + ')';
  }

  function seekTimeline(timeline, time) {
    if (!timeline) return;
    try {
      if (typeof timeline.pause === 'function') timeline.pause();
      if (typeof timeline.totalTime === 'function') {
        timeline.totalTime(time, false);
      } else if (typeof timeline.time === 'function') {
        timeline.time(time, false);
      } else if (typeof timeline.progress === 'function' && durationSeconds > 0) {
        timeline.progress(Math.max(0, Math.min(1, time / durationSeconds)), false);
      }
    } catch (err) {
      console.warn('[hyperframes preview] timeline seek failed', err);
    }
  }

  function seekTimelines(time) {
    var timelines = window.__timelines || {};
    Object.keys(timelines).forEach(function (key) {
      seekTimeline(timelines[key], time);
    });
  }

  function applyClipTiming(time, paused) {
    resolvedStarts = new Map();
    var clips = document.querySelectorAll('[data-start][data-duration], video[data-start], audio[data-start]');
    clips.forEach(function (el) {
      if (el === root) return;

      var start = getClipStart(el, 0);
      var duration = getClipDuration(el);
      var active = duration <= 0 ? time >= start : time >= start && time <= start + duration;
      var tag = el.tagName.toLowerCase();
      var isMedia = tag === 'video' || tag === 'audio';

      if (el.classList.contains('clip') || isMedia) {
        el.style.visibility = active ? 'visible' : 'hidden';
      }

      var trackIndex = toNumber(el.getAttribute('data-track-index'), NaN);
      if (Number.isFinite(trackIndex)) {
        el.style.zIndex = String(trackIndex);
      }

      if (isMedia && active) {
        var mediaOffset = toNumber(el.getAttribute('data-media-start'), 0);
        var localTime = Math.max(0, time - start + mediaOffset);
        if (Number.isFinite(el.duration)) {
          localTime = Math.min(localTime, Math.max(0, el.duration - 0.03));
        }
        try {
          if (Math.abs(el.currentTime - localTime) > 0.2) el.currentTime = localTime;
          if (paused && typeof el.pause === 'function') el.pause();
        } catch {
          // Media may not be seekable yet; the next preview tick will retry.
        }
      }
    });
  }

  function renderAt(time, paused) {
    prepareStage();
    var clamped = Math.max(0, Math.min(durationSeconds, Number(time) || 0));
    seekTimelines(clamped);
    applyClipTiming(clamped, Boolean(paused));
    window.__hyperframesPreviewTime = clamped;
  }

  window.__galexiaHyperframesPreview = { renderAt: renderAt };

  window.addEventListener('message', function (event) {
    var data = event.data || {};
    if (data.type !== 'galexia:hyperframes:setTime') return;
    renderAt(data.time, data.paused);
  });

  window.addEventListener('resize', resizeStage);

  requestAnimationFrame(function () {
    renderAt(0, true);
    window.parent.postMessage({
      type: 'galexia:hyperframes:ready',
      width: compositionWidth,
      height: compositionHeight,
      durationSeconds: durationSeconds
    }, '*');
  });
})();
</script>`.trim();

  return injectBeforeClosingTag(injectAfterOpeningTag(htmlDocument, 'head', prelude), 'body', controller);
}
